import React, { useState, useEffect } from 'react';
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaSave,
  FaTimes,
  FaSpinner,
  FaDollarSign,
  FaPercentage,
  FaCalendar
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';
import { useAdminAuth } from '../context/AdminAuthContext';

const SERVICE_TYPES = [
  // 'nbn' switches the customer-facing agreement page to the telco terms + the full
  // TCP/ACMA/wholesale-schedule cl.7.1 acknowledgement set. Do NOT quote an nbn® service under any
  // other type — the IT terms carry none of the mandatory disclosures.
  { value: 'nbn', label: 'nbn® Service (business)' },
  { value: 'website', label: 'Website Design & Development' },
  { value: 'email', label: 'Email Services' },
  { value: 'branding', label: 'Branding & Logo' },
  { value: 'cloud', label: 'Cloud Services' },
  { value: 'networking', label: 'Networking Solutions' },
  { value: 'support', label: 'Ongoing Premium Support' },
  { value: 'other', label: 'Other' }
];

const PAYMENT_SCHEDULES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

const ITServicesQuoteForm = ({ quote = null, onSave, onCancel }) => {
  const { token } = useAdminAuth();
  const isEditMode = !!quote;

  // createQuote/updateQuote/getQuote are admin-gated in the quote lambda —
  // without this header every save comes back 403 Forbidden.
  const getHeaders = () => ({
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': token } : {})
  });

  // Calculate initial discount type and value
  const getInitialDiscountData = () => {
    if (!quote || !quote.discount || quote.discount === 0) {
      return { discount: 0, discountType: 'amount' };
    }
    
    // The discount is applied to the GST-INCLUSIVE total, so recover that base
    // (stored subtotal/tax are both post-discount) or the inferred percentage is
    // wrong — e.g. 10% off $120 would read back as 12.22%.
    const inclusiveBeforeDiscount = (quote.subtotal || 0) + (quote.tax || 0) + quote.discount;
    if (inclusiveBeforeDiscount === 0) {
      return { discount: quote.discount, discountType: 'amount' };
    }

    // Check if discount looks like a percentage
    const percentage = (quote.discount / inclusiveBeforeDiscount) * 100;
    // If it's a clean percentage (within 0.01%), treat it as percentage
    if (percentage <= 100 && percentage >= 0 && Math.abs(percentage - Math.round(percentage)) < 0.1) {
      return { discount: Math.round(percentage * 100) / 100, discountType: 'percentage' };
    }
    // Otherwise treat as amount
    return { discount: quote.discount, discountType: 'amount' };
  };

  const initialDiscountData = getInitialDiscountData();

  const [formData, setFormData] = useState({
    customerEmail: quote?.customerEmail || '',
    customerName: quote?.customerName || '',
    customerPhone: quote?.customerPhone || '',
    customerCompany: quote?.customerCompany || '',
    serviceType: quote?.serviceType || 'website',
    quoteTitle: quote?.quoteTitle || '',
    quoteDescription: quote?.quoteDescription || '',
    lineItems: quote?.lineItems || [
      { description: '', quantity: 1, unitPrice: 0, total: 0 }
    ],
    paymentType: quote?.paymentType || 'one-time',
    paymentSchedule: quote?.paymentSchedule || 'monthly',
    recurringAmount: quote?.recurringAmount || null,
    totalInstallments: quote?.totalInstallments || null,
    discount: initialDiscountData.discount,
    discountType: initialDiscountData.discountType,
    notes: quote?.notes || '',
    // nbn-only. These drive what the CUSTOMER sees: the line items carry only the
    // part-month amount due now, so without `nbnOngoingMonthly` the quote reads as a
    // one-off $x with no sign of the ongoing commitment — which TCP Code §3.2 requires
    // up front. `nbnCustomerUID` attaches their consent records to the customer;
    // `nbnOrderType` TRANSFER adds the cl.7.1(g) acknowledgement.
    nbnCustomerUID: quote?.metadata?.customerUID || '',
    nbnOrderType: quote?.metadata?.orderType || 'TRANSFER',
    nbnPlan: quote?.metadata?.nbnPlan || '',
    nbnOngoingMonthly: quote?.metadata?.ongoingMonthly ?? '',
    nbnFirstFullBillingDate: quote?.metadata?.firstFullBillingDate || '',
    nbnProRataDays: quote?.metadata?.proRataDays ?? '',
    nbnServiceAddress: quote?.metadata?.serviceAddress || '',
    nbnAvcId: quote?.metadata?.avcId || '',
    nbnLocId: quote?.metadata?.locId || ''
  });

  const [calculations, setCalculations] = useState({
    subtotal: 0,
    discountAmount: 0,
    tax: 0,
    total: 0
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Calculate totals whenever line items or discount changes
  useEffect(() => {
    calculateTotals();
  }, [formData.lineItems, formData.discount, formData.discountType]);

  const calculateTotals = () => {
    // Item prices are GST inclusive
    const totalInclusive = formData.lineItems.reduce((sum, item) => {
      const total = (item.quantity || 1) * (item.unitPrice || 0);
      return sum + total;
    }, 0);

    // Apply discount to the inclusive total
    let discountAmount = 0;
    if (formData.discountType === 'percentage') {
      discountAmount = (totalInclusive * formData.discount) / 100;
    } else {
      discountAmount = formData.discount || 0;
    }

    const afterDiscount = totalInclusive - discountAmount;
    
    // Calculate GST component (GST is 1/11th of the inclusive price in Australia)
    // If price is $1000 GST inclusive, GST = $1000 / 11 = $90.91
    // The user wants to show GST, so we calculate it as 10% of base for display
    // But for accurate calculation: GST = Total / 11
    const tax = afterDiscount / 11;
    
    // Subtotal excluding GST
    const subtotal = afterDiscount - tax;
    
    // Total (same as afterDiscount since it's already inclusive)
    const total = afterDiscount;

    setCalculations({
      subtotal,
      discountAmount,
      tax,
      total
    });
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedItems = [...formData.lineItems];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: field === 'quantity' || field === 'unitPrice' ? parseFloat(value) || 0 : value
    };

    // Calculate total for this line item
    const quantity = updatedItems[index].quantity || 1;
    const unitPrice = updatedItems[index].unitPrice || 0;
    updatedItems[index].total = quantity * unitPrice;

    setFormData({ ...formData, lineItems: updatedItems });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      lineItems: [
        ...formData.lineItems,
        { description: '', quantity: 1, unitPrice: 0, total: 0 }
      ]
    });
  };

  const removeLineItem = (index) => {
    if (formData.lineItems.length > 1) {
      const updatedItems = formData.lineItems.filter((_, i) => i !== index);
      setFormData({ ...formData, lineItems: updatedItems });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    // Validation
    if (!formData.customerEmail || !formData.customerName) {
      setError('Customer email and name are required');
      setSaving(false);
      return;
    }

    if (formData.lineItems.some(item => !item.description || item.unitPrice <= 0)) {
      setError('All line items must have a description and a valid price');
      setSaving(false);
      return;
    }

    if (calculations.total <= 0) {
      setError('Total amount must be greater than 0');
      setSaving(false);
      return;
    }

    try {
      const quoteData = {
        customerEmail: formData.customerEmail,
        customerName: formData.customerName,
        customerPhone: formData.customerPhone || null,
        customerCompany: formData.customerCompany || null,
        serviceType: formData.serviceType,
        quoteTitle: formData.quoteTitle || SERVICE_TYPES.find(s => s.value === formData.serviceType)?.label,
        quoteDescription: formData.quoteDescription,
        lineItems: formData.lineItems.map(item => ({
          description: item.description,
          quantity: item.quantity || 1,
          unitPrice: item.unitPrice || 0,
          total: item.total || 0
        })),
        subtotal: calculations.subtotal,
        tax: calculations.tax,
        // Store the resolved DOLLAR amount, never the raw percentage — and never 0
        // for percentage discounts (the old behaviour), which silently dropped the
        // discount: the total was reduced but the quote page hides its discount row
        // when `discount` is 0, leaving an unexplained gap against the line items.
        discount: calculations.discountAmount,
        totalAmount: calculations.total,
        paymentType: formData.paymentType,
        paymentSchedule: formData.paymentType === 'recurring' ? formData.paymentSchedule : null,
        recurringAmount: formData.paymentType === 'recurring' ? calculations.total : null,
        totalInstallments: formData.paymentType === 'recurring' ? formData.totalInstallments : null,
        notes: formData.notes || null,
        // Only send metadata for nbn quotes — an IT quote has no use for it, and an
        // empty object would overwrite anything already stored on an edit.
        ...(formData.serviceType === 'nbn' ? {
          metadata: {
            ...(quote?.metadata || {}),
            customerUID: formData.nbnCustomerUID || undefined,
            orderType: formData.nbnOrderType || undefined,
            nbnPlan: formData.nbnPlan || undefined,
            ongoingMonthly: formData.nbnOngoingMonthly !== '' ? parseFloat(formData.nbnOngoingMonthly) : undefined,
            firstFullBillingDate: formData.nbnFirstFullBillingDate || undefined,
            proRataDays: formData.nbnProRataDays !== '' ? parseInt(formData.nbnProRataDays, 10) : undefined,
            serviceAddress: formData.nbnServiceAddress || undefined,
            avcId: formData.nbnAvcId || undefined,
            locId: formData.nbnLocId || undefined,
          }
        } : {})
      };

      let payload;
      if (isEditMode) {
        payload = {
          action: 'updateQuote',
          quoteId: quote.quoteId,
          updates: quoteData
        };
      } else {
        payload = {
          action: 'createQuote',
          ...quoteData
        };
      }

      const response = await fetch(API_ENDPOINTS.IT_SERVICES_QUOTE_MANAGEMENT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        // For update, we need to fetch the updated quote
        if (isEditMode) {
          const getResponse = await fetch(API_ENDPOINTS.IT_SERVICES_QUOTE_MANAGEMENT, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({
              action: 'getQuote',
              quoteId: quote.quoteId
            })
          });
          const getData = await getResponse.json();
          if (onSave && getData.success) {
            onSave(getData.quote);
          }
        } else {
          if (onSave && data.quote) {
            onSave(data.quote);
          }
        }
      } else {
        setError(data.error || 'Failed to save quote');
      }
    } catch (err) {
      console.error('Error saving quote:', err);
      setError('Failed to save quote. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
          <p className="text-red-300">{error}</p>
        </div>
      )}

      {/* Customer Information */}
      <div className="bg-[#282828] rounded-lg p-6">
        <h3 className="text-xl font-bold text-[#ebdbb2] mb-4">Customer Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[#a89984] text-sm mb-2">Customer Name *</label>
            <input
              type="text"
              required
              value={formData.customerName}
              onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
              className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-[#a89984] text-sm mb-2">Customer Email(s) *</label>
            <textarea
              required
              value={formData.customerEmail}
              onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
              className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
              placeholder="customer@example.com, another@example.com"
              rows="2"
            />
            <p className="text-xs text-[#a89984] mt-1">
              Enter multiple email addresses separated by commas
            </p>
          </div>
          <div>
            <label className="block text-[#a89984] text-sm mb-2">Customer Phone</label>
            <input
              type="tel"
              value={formData.customerPhone}
              onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
              className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
              placeholder="+61 400 000 000"
            />
          </div>
          <div>
            <label className="block text-[#a89984] text-sm mb-2">Company</label>
            <input
              type="text"
              value={formData.customerCompany}
              onChange={(e) => setFormData({ ...formData, customerCompany: e.target.value })}
              className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
              placeholder="Company Name"
            />
          </div>
        </div>
      </div>

      {/* Service Information */}
      <div className="bg-[#282828] rounded-lg p-6">
        <h3 className="text-xl font-bold text-[#ebdbb2] mb-4">Service Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[#a89984] text-sm mb-2">Service Type *</label>
            <select
              required
              value={formData.serviceType}
              onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
              className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
            >
              {SERVICE_TYPES.map(service => (
                <option key={service.value} value={service.value}>{service.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[#a89984] text-sm mb-2">Quote Title</label>
            <input
              type="text"
              value={formData.quoteTitle}
              onChange={(e) => setFormData({ ...formData, quoteTitle: e.target.value })}
              className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
              placeholder="Auto-filled from service type"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-[#a89984] text-sm mb-2">Description</label>
          <textarea
            value={formData.quoteDescription}
            onChange={(e) => setFormData({ ...formData, quoteDescription: e.target.value })}
            rows="3"
            className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
            placeholder="Describe the services to be provided..."
          />
        </div>
      </div>

      {/* nbn® service details — drives the customer-facing quote + agreement pages */}
      {formData.serviceType === 'nbn' && (
        <div className="bg-[#282828] rounded-lg p-6 border border-[#FFD700]/40">
          <h3 className="text-xl font-bold text-[#ebdbb2] mb-1">nbn&reg; service details</h3>
          <p className="text-[#a89984] text-sm mb-4">
            The line items above are the amount due <strong>now</strong> (the part month).
            These fields tell the customer what they&apos;re signing up to ongoing — without
            the monthly charge the quote reads as a one-off and discloses nothing.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#a89984] text-sm mb-2">Ongoing monthly charge (GST inc.) *</label>
              <input
                type="number" step="0.01"
                value={formData.nbnOngoingMonthly}
                onChange={(e) => setFormData({ ...formData, nbnOngoingMonthly: e.target.value })}
                className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                placeholder="110.00"
              />
              <p className="text-[#a89984] text-xs mt-1">Shown larger than the amount due now (TCP Code §3.2).</p>
            </div>
            <div>
              <label className="block text-[#a89984] text-sm mb-2">First full billing date</label>
              <input
                type="date"
                value={formData.nbnFirstFullBillingDate}
                onChange={(e) => setFormData({ ...formData, nbnFirstFullBillingDate: e.target.value })}
                className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
              />
              <p className="text-[#a89984] text-xs mt-1">Usually the 1st of next month.</p>
            </div>
            <div>
              <label className="block text-[#a89984] text-sm mb-2">Pro-rata days covered</label>
              <input
                type="number"
                value={formData.nbnProRataDays}
                onChange={(e) => setFormData({ ...formData, nbnProRataDays: e.target.value })}
                className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                placeholder="5"
              />
            </div>
            <div>
              <label className="block text-[#a89984] text-sm mb-2">Plan name (as shown to customer)</label>
              <input
                type="text"
                value={formData.nbnPlan}
                onChange={(e) => setFormData({ ...formData, nbnPlan: e.target.value })}
                className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                placeholder="NBN 100/40 Business + Static IP"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[#a89984] text-sm mb-2">Service address</label>
              <input
                type="text"
                value={formData.nbnServiceAddress}
                onChange={(e) => setFormData({ ...formData, nbnServiceAddress: e.target.value })}
                className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                placeholder="Tenancy 5, Level 2, 1159 Sandgate Road, Nundah QLD 4012"
              />
            </div>
            <div>
              <label className="block text-[#a89984] text-sm mb-2">Order type</label>
              <select
                value={formData.nbnOrderType}
                onChange={(e) => setFormData({ ...formData, nbnOrderType: e.target.value })}
                className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
              >
                <option value="TRANSFER">Transfer (existing service)</option>
                <option value="NEW">New connection</option>
              </select>
              <p className="text-[#a89984] text-xs mt-1">
                Transfer adds the required &ldquo;my current service will be disconnected&rdquo; acknowledgement.
              </p>
            </div>
            <div>
              <label className="block text-[#a89984] text-sm mb-2">Customer UID *</label>
              <input
                type="text"
                value={formData.nbnCustomerUID}
                onChange={(e) => setFormData({ ...formData, nbnCustomerUID: e.target.value })}
                className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700] font-mono text-xs"
                placeholder="from New Business Customer"
              />
              <p className="text-[#a89984] text-xs mt-1">
                Attaches their consent records to the customer. Without it they file under the quote.
              </p>
            </div>
            <div>
              <label className="block text-[#a89984] text-sm mb-2">AVC ID</label>
              <input
                type="text"
                value={formData.nbnAvcId}
                onChange={(e) => setFormData({ ...formData, nbnAvcId: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                placeholder="AVC000012345678"
              />
              {formData.nbnAvcId && !/^AVC\d{12}$/i.test(formData.nbnAvcId.trim()) ? (
                <p className="text-red-400 text-xs mt-1">Must be AVC followed by 12 digits.</p>
              ) : null}
            </div>
            <div>
              <label className="block text-[#a89984] text-sm mb-2">LOC ID</label>
              <input
                type="text"
                value={formData.nbnLocId}
                onChange={(e) => setFormData({ ...formData, nbnLocId: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                placeholder="LOC000012345678"
              />
              {formData.nbnLocId && !/^LOC\d{12}$/i.test(formData.nbnLocId.trim()) ? (
                <p className="text-red-400 text-xs mt-1">Must be LOC followed by 12 digits.</p>
              ) : null}
            </div>
          </div>

          {formData.nbnOngoingMonthly === '' ? (
            <p className="text-red-400 text-sm mt-4">
              ⚠️ Without an ongoing monthly charge the customer sees only the amount due now,
              with no disclosure of what they pay each month.
            </p>
          ) : null}
        </div>
      )}

      {/* Line Items */}
      <div className="bg-[#282828] rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-[#ebdbb2]">Line Items</h3>
          <button
            type="button"
            onClick={addLineItem}
            className="bg-green-500/20 text-green-300 px-4 py-2 rounded-lg hover:bg-green-500/30 transition-colors flex items-center space-x-2"
          >
            <FaPlus />
            <span>Add Item</span>
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#504945]">
                <th className="text-left py-3 px-4 text-[#a89984] text-sm font-semibold">Description</th>
                <th className="text-right py-3 px-4 text-[#a89984] text-sm font-semibold">Quantity</th>
                <th className="text-right py-3 px-4 text-[#a89984] text-sm font-semibold">Unit Price</th>
                <th className="text-right py-3 px-4 text-[#a89984] text-sm font-semibold">Total</th>
                <th className="text-center py-3 px-4 text-[#a89984] text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {formData.lineItems.map((item, index) => (
                <tr key={index} className="border-b border-[#504945]">
                  <td className="py-3 px-4">
                    <input
                      type="text"
                      required
                      value={item.description}
                      onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 bg-[#3c3836] border border-[#504945] rounded text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                      placeholder="Service description"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={item.quantity || 1}
                      onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                      className="w-full px-3 py-2 bg-[#3c3836] border border-[#504945] rounded text-[#ebdbb2] text-right focus:outline-none focus:border-[#FFD700]"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center">
                      <span className="text-[#a89984] mr-2">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unitPrice || 0}
                        onChange={(e) => handleLineItemChange(index, 'unitPrice', e.target.value)}
                        className="flex-1 px-3 py-2 bg-[#3c3836] border border-[#504945] rounded text-[#ebdbb2] text-right focus:outline-none focus:border-[#FFD700]"
                      />
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right text-[#ebdbb2] font-semibold">
                    {formatCurrency(item.total || 0)}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {formData.lineItems.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLineItem(index)}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        title="Remove item"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Subtotal */}
        <div className="mt-4 pt-4 border-t border-[#504945]">
          <div className="flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between text-[#ebdbb2]">
                <span>Subtotal:</span>
                <span>{formatCurrency(calculations.subtotal)}</span>
              </div>

              {/* Discount */}
              <div className="bg-[#3c3836] rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[#a89984] text-sm">Discount:</label>
                  <div className="flex items-center space-x-2">
                    <select
                      value={formData.discountType}
                      onChange={(e) => setFormData({ ...formData, discountType: e.target.value, discount: 0 })}
                      className="px-2 py-1 bg-[#282828] border border-[#504945] rounded text-[#ebdbb2] text-xs focus:outline-none focus:border-[#FFD700]"
                    >
                      <option value="amount">$</option>
                      <option value="percentage">%</option>
                    </select>
                    {formData.discountType === 'percentage' ? (
                      <div className="flex items-center">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={formData.discount || 0}
                          onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                          className="w-20 px-2 py-1 bg-[#282828] border border-[#504945] rounded text-[#ebdbb2] text-right focus:outline-none focus:border-[#FFD700]"
                        />
                        <span className="text-[#a89984] ml-1">%</span>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <span className="text-[#a89984] mr-1">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.discount || 0}
                          onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                          className="w-24 px-2 py-1 bg-[#282828] border border-[#504945] rounded text-[#ebdbb2] text-right focus:outline-none focus:border-[#FFD700]"
                        />
                      </div>
                    )}
                  </div>
                </div>
                {calculations.discountAmount > 0 && (
                  <div className="flex justify-between text-green-300 text-sm">
                    <span>Discount Amount:</span>
                    <span>-{formatCurrency(calculations.discountAmount)}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-between text-[#ebdbb2]">
                <span>Tax (GST 10% - Included):</span>
                <span>{formatCurrency(calculations.tax)}</span>
              </div>
              <div className="text-[#a89984] text-xs text-right">
                Subtotal (excl. GST): {formatCurrency(calculations.subtotal)}
              </div>
              <div className="flex justify-between text-[#ebdbb2] font-bold text-xl pt-2 border-t border-[#504945]">
                <span>Total:</span>
                <span className="text-[#FFD700]">{formatCurrency(calculations.total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Terms */}
      <div className="bg-[#282828] rounded-lg p-6">
        <h3 className="text-xl font-bold text-[#ebdbb2] mb-4">Payment Terms</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[#a89984] text-sm mb-2">Payment Type *</label>
            <select
              required
              value={formData.paymentType}
              onChange={(e) => setFormData({ ...formData, paymentType: e.target.value })}
              className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
            >
              <option value="one-time">One-time Payment</option>
              <option value="recurring">Recurring Payment</option>
            </select>
          </div>

          {formData.paymentType === 'recurring' && (
            <>
              <div>
                <label className="block text-[#a89984] text-sm mb-2">Payment Schedule</label>
                <select
                  value={formData.paymentSchedule}
                  onChange={(e) => setFormData({ ...formData, paymentSchedule: e.target.value })}
                  className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                >
                  {PAYMENT_SCHEDULES.map(schedule => (
                    <option key={schedule.value} value={schedule.value}>{schedule.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[#a89984] text-sm mb-2">Number of Installments</label>
                <input
                  type="number"
                  min="1"
                  value={formData.totalInstallments || ''}
                  onChange={(e) => setFormData({ ...formData, totalInstallments: parseInt(e.target.value) || null })}
                  className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                  placeholder="e.g., 12 for 12 months"
                />
              </div>
              <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-3">
                <div className="text-blue-300 text-sm">
                  <strong>Recurring Amount:</strong> {formatCurrency(calculations.total)} per {formData.paymentSchedule}
                </div>
                {formData.totalInstallments && (
                  <div className="text-blue-200 text-xs mt-1">
                    Total over {formData.totalInstallments} installments: {formatCurrency(calculations.total * formData.totalInstallments)}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="bg-[#282828] rounded-lg p-6">
        <h3 className="text-xl font-bold text-[#ebdbb2] mb-4">Internal Notes</h3>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows="3"
          className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
          placeholder="Internal notes (not visible to customer)..."
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 bg-[#504945] text-[#ebdbb2] rounded-lg font-semibold hover:bg-[#665c54] transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#FFB700] text-[#1d2021] rounded-lg font-semibold hover:shadow-lg transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <FaSpinner className="animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <FaSave />
              <span>{isEditMode ? 'Update Quote' : 'Create Quote'}</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default ITServicesQuoteForm;

